import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

function getGreetingCardMessageSyncPlan(cart, line, quantity) {
  return window.SunlitGreetingCardMessageCoreV5?.getGreetingCardMessageSyncPlan(cart, line, quantity) ?? null;
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    queueMicrotask(() => this.#removeInitialOrphanGiftWrap());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  async #removeInitialOrphanGiftWrap() {
    const rows = [...this.querySelectorAll('.cart-items__table-row[data-key]')];
    if (!rows.length || rows.some((row) => row.dataset.sunlitGiftWrap !== 'true')) return;

    const sections = this.#getSectionsToUpdate();
    const cart = {
      items: rows.map((row) => ({
        key: row.dataset.key,
        properties: { _sunlit_gift_wrap: 'true' },
      })),
    };

    try {
      const cleanup = await this.#removeOrphanGiftWrap(cart, sections);
      if (!cleanup.removed) return;
      const sectionHtml = cleanup.cart.sections?.[this.sectionId];
      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }
      this.#showGiftWrapOrphanWarning();
    } catch (error) {
      console.warn('[sunlit-gift-wrap] Could not remove orphan gift wrap:', error);
    }
  }

  #getSectionsToUpdate() {
    const sectionIds = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    });
    return [...sectionIds].join(',');
  }

  async #removeOrphanGiftWrap(cart, sections) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const giftWrapItems = items.filter((item) => item.properties?._sunlit_gift_wrap === 'true');
    const hasNormalProduct = items.some((item) => item.properties?._sunlit_gift_wrap !== 'true');
    if (!giftWrapItems.length || hasNormalProduct) return { cart, removed: false };

    const updates = Object.fromEntries(giftWrapItems.map((item) => [item.key, 0]));
    const body = JSON.stringify({
      updates,
      sections,
      sections_url: window.location.pathname,
    });
    const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
    const updatedCart = JSON.parse(await response.text());
    if (!response.ok || updatedCart.errors) {
      throw new Error(updatedCart.errors || 'Could not remove gift wrap from the empty order.');
    }
    return { cart: updatedCart, removed: true };
  }

  #showGiftWrapOrphanWarning() {
    document.querySelector('[data-sunlit-gift-wrap-orphan-warning]')?.remove();
    const warning = document.createElement('div');
    warning.setAttribute('data-sunlit-gift-wrap-orphan-warning', '');
    warning.setAttribute('role', 'alert');
    warning.setAttribute('aria-live', 'assertive');
    warning.style.cssText = [
      'position:absolute',
      'top:4.75rem',
      'right:1rem',
      'left:1rem',
      'bottom:auto',
      'z-index:2147483647',
      'width:auto',
      'padding:0.9rem 2.75rem 0.9rem 1rem',
      'border:1px solid #9f5364',
      'border-radius:0.75rem',
      'background:#eee0e0',
      'color:#4a2029',
      'box-shadow:0 0.75rem 2rem rgba(64,24,34,0.22)',
      'font:600 0.9rem/1.4 var(--font-body--family,inherit)',
      'box-sizing:border-box',
    ].join(';');
    warning.textContent = 'Gift wrap was removed. At least one product is needed to add gift wrap.';

    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss gift wrap message');
    close.textContent = '×';
    close.style.cssText = 'position:absolute;top:0.4rem;right:0.55rem;padding:0.15rem 0.35rem;border:0;background:transparent;color:inherit;font:700 1.4rem/1;cursor:pointer';
    close.addEventListener('click', () => warning.remove());
    warning.append(close);
    const warningHost = document.querySelector('#cart-drawer dialog[open]') || document.querySelector('#cart-drawer dialog') || document.body;
    warningHost.append(warning);
    window.setTimeout(() => warning.remove(), 12000);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    let body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      let letterSheetSyncPlan = null;
      let greetingMessageSyncPlan = null;
      try {
        const cartBeforeChange = await this.fetchCartData();
        letterSheetSyncPlan = this.#getLetterSheetSyncPlan(cartBeforeChange, line, quantity);
        greetingMessageSyncPlan = getGreetingCardMessageSyncPlan(cartBeforeChange, line, quantity);
      } catch (error) {
        console.warn('[cart-personalisation] Could not inspect cart before update; continuing with normal cart change.', error);
      }

      // Rewrite the main letter first so there is never a cart state where the
      // paid sheet is gone but the old character allowance remains.
      if (letterSheetSyncPlan) {
        const syncBody = JSON.stringify({
          id: letterSheetSyncPlan.mainKey,
          quantity: letterSheetSyncPlan.mainQuantity,
          properties: letterSheetSyncPlan.properties,
        });
        const syncResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: syncBody }));
        const syncResult = JSON.parse(await syncResponse.text());

        if (syncResult.errors) {
          throw new Error(syncResult.errors);
        }

        // Updating line-item properties regenerates the main letter key. Target
        // the paid-sheet line by its own preserved key rather than a line index.
        body = JSON.stringify({
          id: letterSheetSyncPlan.changedKey,
          quantity,
          sections,
          sections_url: window.location.pathname,
        });
      }

      // Keep the paid greeting-card message line and linked main product in sync.
      // The linked line changes first so customers cannot retain an unpaid message.
      if (greetingMessageSyncPlan) {
        if (greetingMessageSyncPlan.preMutation) {
          const greetingSyncBody = JSON.stringify(greetingMessageSyncPlan.preMutation);
          const greetingSyncResponse = await fetch(
            `${Theme.routes.cart_change_url}`,
            fetchConfig('json', { body: greetingSyncBody })
          );
          const greetingSyncResult = JSON.parse(await greetingSyncResponse.text());

          if (greetingSyncResult.errors) {
            throw new Error(greetingSyncResult.errors);
          }
        }

        body = JSON.stringify({
          id: greetingMessageSyncPlan.changedKey,
          quantity: greetingMessageSyncPlan.changedQuantity,
          sections,
          sections_url: window.location.pathname,
        });
      }

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      let parsedResponseText = JSON.parse(await response.text());

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        throw new Error(parsedResponseText.errors);
      }

      const giftWrapCleanup = await this.#removeOrphanGiftWrap(parsedResponseText, sections);
      parsedResponseText = giftWrapCleanup.cart;

      resetShimmer(this);

      const sectionHtml = parsedResponseText.sections?.[this.sectionId];
      const newSectionHTML = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : null;
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount
        ? parseInt(newCartHiddenItemCount, 10)
        : Number(parsedResponseText.item_count || 0);

      this.#updateQuantitySelectors(parsedResponseText);

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: parsedResponseText.sections,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }

      this.#updateCartQuantitySelectorButtonStates();
      if (giftWrapCleanup.removed) this.#showGiftWrapOrphanWarning();
      if (letterSheetSyncPlan) this.#showLetterSheetWarning(letterSheetSyncPlan);
      if (greetingMessageSyncPlan?.warning) this.#showGreetingMessageWarning(greetingMessageSyncPlan.warning);
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Builds the main-letter property update required when a linked paid sheet line changes.
   * @param {any} cart
   * @param {number} line
   * @param {number} requestedQuantity
   * @returns {{mainKey: string, changedKey: string, mainQuantity: number, properties: Record<string, string>, removedCharacters: number, allowanceReduction: number, removedSheets: number, newLimit: number, removedAll: boolean} | null}
   */
  #getLetterSheetSyncPlan(cart, line, requestedQuantity) {
    const changedItem = cart?.items?.[line - 1];
    const changedProperties = changedItem?.properties || {};
    const linkedVariant = String(changedProperties['_Linked letter variant'] || '');

    if (!changedItem || !linkedVariant || changedProperties['_Extra sheets per letter'] == null) return null;

    const linkedTitle = String(changedProperties['_For letter product'] || '');
    const oldExtraSheets = Math.max(0, parseInt(changedProperties['_Extra sheets per letter'], 10) || 0);
    const candidates = cart.items.filter((item) => {
      const properties = item.properties || {};
      return (
        item.key !== changedItem.key &&
        String(item.variant_id) === linkedVariant &&
        properties['Letter Character Limit'] != null &&
        properties['Extra Letter Sheets'] != null
      );
    });

    const mainItem =
      candidates.find((item) => {
        const properties = item.properties || {};
        const titleMatches = !linkedTitle || item.product_title === linkedTitle;
        return titleMatches && (parseInt(properties['Extra Letter Sheets'], 10) || 0) === oldExtraSheets;
      }) || candidates.find((item) => !linkedTitle || item.product_title === linkedTitle) || candidates[0];

    if (!mainItem) return null;

    const mainProperties = { ...(mainItem.properties || {}) };
    const mainQuantity = Math.max(1, Number(mainItem.quantity) || 1);
    const remainingExtraSheets = Math.max(0, Math.floor(Math.max(0, requestedQuantity) / mainQuantity));
    const baseLimit = 1000;
    const charactersPerPaidSheet = 1000;
    const oldLimit = Math.max(
      baseLimit,
      parseInt(mainProperties['Letter Character Limit'], 10) || baseLimit + oldExtraSheets * charactersPerPaidSheet
    );
    const newLimit = baseLimit + remainingExtraSheets * charactersPerPaidSheet;
    const oldMessage = typeof mainProperties['Letter Message'] === 'string' ? mainProperties['Letter Message'] : '';
    const newMessage = oldMessage.slice(0, newLimit);

    mainProperties['Extra Letter Sheets'] = String(remainingExtraSheets);
    mainProperties['Total Letter Sheets'] = String(remainingExtraSheets + 1);
    mainProperties['Letter Character Limit'] = String(newLimit);
    if (typeof mainProperties['Letter Message'] === 'string') {
      mainProperties['Letter Message'] = newMessage;
    }

    return {
      mainKey: mainItem.key,
      changedKey: changedItem.key,
      mainQuantity,
      properties: mainProperties,
      removedCharacters: Math.max(0, oldMessage.length - newMessage.length),
      allowanceReduction: Math.max(0, oldLimit - newLimit),
      removedSheets: Math.max(0, oldExtraSheets - remainingExtraSheets),
      newLimit,
      removedAll: requestedQuantity === 0,
    };
  }

  /**
   * Shows an accessible, dismissible warning after paid sheet capacity is reduced.
   * @param {{removedCharacters: number, allowanceReduction: number, removedSheets: number, newLimit: number, removedAll: boolean}} plan
   */
  #showLetterSheetWarning(plan) {
    if (plan.removedSheets <= 0) return;

    document.querySelector('[data-sunlit-letter-sheet-warning-v2]')?.remove();

    const warning = document.createElement('div');
    warning.setAttribute('data-sunlit-letter-sheet-warning-v2', '');
    warning.setAttribute('role', 'alert');
    warning.setAttribute('aria-live', 'assertive');
    warning.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:1rem',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'display:flex',
      'align-items:flex-start',
      'gap:0.75rem',
      'width:min(32rem,calc(100vw - 2rem))',
      'padding:1rem',
      'border:1px solid #c4455c',
      'border-radius:0.75rem',
      'background:#fff8f4',
      'color:#4a2029',
      'box-shadow:0 0.75rem 2rem rgba(64,24,34,0.24)',
      'font:600 0.95rem/1.45 var(--font-body--family,inherit)',
      'box-sizing:border-box',
    ].join(';');

    const message = document.createElement('span');
    const action = plan.removedAll ? 'Extra letter sheet removed.' : 'Extra letter sheet quantity reduced.';
    if (plan.removedCharacters > 0) {
      message.textContent = `${action} Your letter has been reduced by ${plan.removedCharacters.toLocaleString('en-IN')} characters. ${plan.newLimit.toLocaleString('en-IN')} characters remain.`;
    } else {
      message.textContent = `${action} Your character limit is now ${plan.newLimit.toLocaleString('en-IN')} characters. No letter text was removed.`;
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss letter character warning');
    close.textContent = '×';
    close.style.cssText = 'margin:-0.35rem -0.25rem 0 auto;padding:0.15rem 0.35rem;border:0;background:transparent;color:inherit;font:700 1.5rem/1;cursor:pointer';
    close.addEventListener('click', () => warning.remove());

    warning.append(message, close);
    const warningHost = this.closest('theme-drawer, dialog, [popover]') || document.body;
    warningHost.append(warning);
    window.setTimeout(() => warning.remove(), 15000);
  }

  /**
   * Shows an accessible warning when the paid greeting-card message is removed.
   * @param {string} warningText
   */
  #showGreetingMessageWarning(warningText) {
    document.querySelector('[data-sunlit-greeting-message-warning-v1]')?.remove();

    const warning = document.createElement('div');
    warning.setAttribute('data-sunlit-greeting-message-warning-v1', '');
    warning.setAttribute('role', 'alert');
    warning.setAttribute('aria-live', 'assertive');
    warning.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:1rem',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'display:flex',
      'align-items:flex-start',
      'gap:0.75rem',
      'width:min(32rem,calc(100vw - 2rem))',
      'padding:1rem',
      'border:1px solid #c4455c',
      'border-radius:0.75rem',
      'background:#fff8f4',
      'color:#4a2029',
      'box-shadow:0 0.75rem 2rem rgba(64,24,34,0.24)',
      'font:600 0.95rem/1.45 var(--font-body--family,inherit)',
      'box-sizing:border-box',
    ].join(';');

    const message = document.createElement('span');
    message.textContent = warningText;

    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss greeting card message warning');
    close.textContent = '×';
    close.style.cssText = 'margin:-0.35rem -0.25rem 0 auto;padding:0.15rem 0.35rem;border:0;background:transparent;color:inherit;font:700 1.5rem/1;cursor:pointer';
    close.addEventListener('click', () => warning.remove());

    warning.append(message, close);
    const warningHost = this.closest('theme-drawer, dialog, [popover]') || document.body;
    warningHost.append(warning);
    window.setTimeout(() => warning.remove(), 15000);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
